import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import { Button } from '@/components/ui/button';
import FilterIcon from '@/components/icons/FilterIcon';
import AddIcon from '@/components/icons/AddIcon';
import NewsAndOffersCard from '@/features/news-and-offers/components/NewsAndOffersCard/NewsAndOffersCard';
import { useEffect, useRef, useState } from 'react';
import NewAndOfferDetails from '@/features/news-and-offers/components/NewAndOfferDetails';
import NewAndOfferManage from '@/features/news-and-offers/components/NewAndOfferManage ';
import NewsAndOffersPageFilter from '@/features/news-and-offers/components/NewsAndOffersPageFilter';
import NewsAndOffersPageEmpty from '@/features/news-and-offers/components/NewsAndOffersPageEmpty';
import NewsAndOffersCardSkeleton from '@/features/news-and-offers/components/NewsAndOffersCard/NewsAndOffersCardSkeleton';
import { useNewsAndOffersListApi } from '@/features/news-and-offers/hooks/useNewsAndOffersListApi';
import { useNewsAndOffersFilter } from '@/features/news-and-offers/hooks/useNewsAndOffersFilter';
import { FilterHelper } from '@/utils/filterHelper';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types.ts';
import NewsAndOffersCardMobileSkeleton from './components/NewsAndOffersCard/NewsAndOffersCardMobileSkeleton';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import NewsAndOffersCardMobile from './components/NewsAndOffersCard/NewsAndOffersCardMobile';

const NewsAndOffersPage = () => {
	const [newAndOfferId, setNewAndOfferId] = useState('');
	const [showNewAndOfferDetails, setShowNewAndOfferDetails] = useState(false);
	const [showNewAndOfferManage, setShowNewAndOfferManage] = useState(false);
	const [showNewAndOfferFilter, setShowNewAndOfferFilter] = useState(false);

	const loaderRef = useRef<HTMLDivElement | null>(null);
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const userRole = useUserRole();

	const { filter } = useNewsAndOffersFilter();

	const {
		newsAndOffersListData,
		isLoadingNewsAndOffersListData,
		fetchNewsAndOffersListDataNextPage,
		hasNewsAndOffersListDataNextPage,
		isFetchingNewsAndOffersListDataNextPage,
	} = useNewsAndOffersListApi({
		take: 20,
		startDate: filter.startDate ?? undefined,
		endDate: filter.endDate ?? undefined,
		types: filter.types,
		statuses: filter.statuses,
	});

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasNewsAndOffersListDataNextPage ||
			isFetchingNewsAndOffersListDataNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchNewsAndOffersListDataNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '500px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchNewsAndOffersListDataNextPage,
		hasNewsAndOffersListDataNextPage,
		isFetchingNewsAndOffersListDataNextPage,
	]);

	const handleShowDetails = (id: string) => {
		setNewAndOfferId(id);
		setShowNewAndOfferDetails(true);
	};

	const handleCreateNew = () => {
		setNewAndOfferId('');
		setShowNewAndOfferManage(true);
	};

	const handleEditSelected = (id: string) => {
		setNewAndOfferId(id);
		setShowNewAndOfferManage(true);
	};

	const filtersQuantity = FilterHelper.countFilterValues(filter);

	return (
		<section
			className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
			<EPageHeader
				title={t('main.newsAndOffers.title')}
				addBackButton={userRole === Roles.owner}>
				<div className='relative'>
					{!!filtersQuantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{filtersQuantity}
						</div>
					)}
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowNewAndOfferFilter(true)}>
						<FilterIcon />
						{!isMobile && t('elements.buttons.filter')}
					</Button>
				</div>
				{userRole === Roles.admin && (
					<Button
						size={isMobile ? 'icon' : 'default'}
						onClick={handleCreateNew}>
						<AddIcon className='stroke-popover-10' />
						{!isMobile && t('elements.buttons.createNew')}
					</Button>
				)}
			</EPageHeader>
			{!!isLoadingNewsAndOffersListData && (
				<div className='h-full grid gap-4 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]'>
					{Array.from({ length: 20 }).map((_, index) =>
						isMobile ? (
							<NewsAndOffersCardMobileSkeleton
								key={index}
								footerButtons={userRole}
							/>
						) : (
							<NewsAndOffersCardSkeleton key={index} />
						),
					)}
				</div>
			)}
			{!isLoadingNewsAndOffersListData &&
				!!newsAndOffersListData?.length && (
					<div
						className={cn(
							'h-fit grid gap-4 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]',
							isMobile && 'gap-0',
						)}>
						{newsAndOffersListData.map((offer) =>
							isMobile ? (
								<NewsAndOffersCardMobile
									key={offer.id}
									{...offer}
									showDetails={handleShowDetails}
									onEdit={handleEditSelected}
								/>
							) : (
								<NewsAndOffersCard
									key={offer.id}
									{...offer}
									showDetails={handleShowDetails}
									onEdit={handleEditSelected}
								/>
							),
						)}
					</div>
				)}
			{!isLoadingNewsAndOffersListData &&
				!newsAndOffersListData?.length && <NewsAndOffersPageEmpty />}
			<div
				ref={loaderRef}
				className='h-px'
			/>
			{showNewAndOfferDetails && (
				<NewAndOfferDetails
					id={newAndOfferId}
					onClose={() => setShowNewAndOfferDetails(false)}
				/>
			)}
			{showNewAndOfferManage && (
				<NewAndOfferManage
					id={newAndOfferId}
					onClose={() => setShowNewAndOfferManage(false)}
				/>
			)}
			{showNewAndOfferFilter && (
				<NewsAndOffersPageFilter
					onClose={() => setShowNewAndOfferFilter(false)}
				/>
			)}
		</section>
	);
};

export default NewsAndOffersPage;
